import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Tag, Track } from '@app/core/models/Track';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { TrackApiModel } from '@app/library/services/track.service';
import {
  ResetEditingTracks,
  SetActionAfterEditTrack,
} from '@app/library/states/editing-track.actions';
import { EditingTrackState } from '@app/library/states/editing-track.state';
import { RadioTagsState } from '@app/library/states/radio-tags.state';
import {
  TrackBuyLinkLookupFailure,
  TrackBuyLinkLookupRequest,
  TrackBuyLinkLookupSuccess,
  TrackEditRequest,
  TrackTagsSaveRequest,
} from '@app/library/states/tracks.actions';
import { notOnlySpacesRegExp, urlRegex } from '@app/shared/custom-validators';
import { isEqualFormData, mSanitize } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
  ClipboardService,
  ErrorFormFormat,
  FormHelperService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { DynamicEnabledPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import amplitude from 'amplitude-js';
import { cloneDeep } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, take } from 'rxjs/operators';

@Component({
  selector: 'rk-library-track-edit',
  templateUrl: './library-track-edit.component.html',
  styleUrls: ['./library-track-edit.component.scss'],
})
export class LibraryTrackEditComponent implements OnInit, OnDestroy, DynamicEnabledPanel {
  trackEditForm: UntypedFormGroup;
  trackEditFormErrors: ErrorFormFormat;

  @Select(EditingTrackState.tracks)
  editingTracks$: Observable<Track[]>;

  @Select(RadioTagsState.tagsFormatted)
  tags$: Observable<SugestionInput[]>;

  @Input()
  desktop = true;

  isSearchingBuyLink$: Observable<boolean>;

  modalTitle = '';
  isMultiple = false;

  subscription = new Subscription();

  initialFormData: Partial<Track> = {};

  hasSendForm = false;

  readonly autoFocusDelayMs = 450; // Wait until sidenav transition end

  constructor(
    private readonly formHelper: FormHelperService,
    private readonly clipboardService: ClipboardService,
    private readonly store: Store,
    private readonly fb: UntypedFormBuilder,
    private readonly action$: Actions,
    private readonly translate: TranslateService,
    private readonly uiHelper: UiStoreHelperService,
    private readonly modalService: LibraryModalService,
  ) {}

  ngOnInit() {
    this.isSearchingBuyLink$ = this.uiHelper.getLoadingState(
      [TrackBuyLinkLookupRequest],
      [TrackBuyLinkLookupSuccess, TrackBuyLinkLookupFailure],
    );

    this.subscription.add(
      this.isSearchingBuyLink$.subscribe(data => {
        if (this.trackEditForm) {
          if (data) {
            this.trackEditForm.get('buyLink').disable();
          } else {
            this.trackEditForm.get('buyLink').enable();
          }
        }
      }),
    );

    this.subscription.add(
      this.editingTracks$.subscribe(data => {
        if (data.length === 0) {
          return;
        }

        this.hasSendForm = false;
        if (data.length < 2) {
          this.isMultiple = false;
          this.createForm(data[0]);
        } else {
          this.isMultiple = true;
          this.createFormMultiple(data);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  findLink(): void {
    this.action$
      .pipe(ofActionDispatched(TrackBuyLinkLookupSuccess), first())
      .subscribe((action: TrackBuyLinkLookupSuccess) => {
        this.trackEditForm.get('buyLink').setValue(action.link);
        this.trackEditForm.get('buyLink').markAsPristine();
      });

    this.editingTracks$
      .pipe(
        take(1),
        filter(val => val.length === 1),
        map(val => val[0]),
      )
      .subscribe(val => this.store.dispatch(new TrackBuyLinkLookupRequest(val)));
  }

  private createForm(track: Track) {
    this.modalTitle = track.title;
    this.setFormErrors();

    this.trackEditForm = this.fb.group({
      title: [
        track.title,
        [Validators.required, Validators.pattern(notOnlySpacesRegExp)],
      ],
      artist: track.artist,
      album: track.album,
      year: [track.year, Validators.pattern(/^\d+$/)],
      buyLink: [track.buyLink, Validators.pattern(urlRegex)],
      tags: [this.convertTagsToSuggestion(track.tags)],
      bpm: [track.bpm, Validators.pattern(/^\d+$/)],
    });

    this.initialFormData = cloneDeep({ ...this.trackEditForm.getRawValue() });

    this.subscription.add(
      this.trackEditForm.valueChanges.subscribe(() => {
        this.formHelper.updateErrorMapping(this.trackEditForm, this.trackEditFormErrors);
      }),
    );
  }

  private convertTagsToSuggestion(tags: Tag[]): SugestionInput[] {
    return tags.map((tag, i) => ({ id: i, name: tag.name, color: tag.color }));
  }

  private createFormMultiple(tracks: Track[]) {
    this.modalTitle = this.translate.instant('MEDIA.EDIT_TRACK.MODAL_TITLE', {
      count: tracks.length,
    });
    this.setFormErrors();
    this.trackEditForm = this.fb.group({
      title: '',
      artist: '',
      album: '',
      year: ['', Validators.pattern(/^\d+$/)],
      buyLink: ['', Validators.pattern(urlRegex)],
      bpm: ['', Validators.pattern(/^\d+$/)],
    });

    this.initialFormData = cloneDeep({ ...this.trackEditForm.getRawValue() });

    this.subscription.add(
      this.trackEditForm.valueChanges.subscribe(() => {
        this.formHelper.updateErrorMapping(this.trackEditForm, this.trackEditFormErrors);
      }),
    );
  }

  private setFormErrors() {
    this.trackEditFormErrors = {
      title: {},
      artist: {},
      album: {},
      year: {},
      buyLink: {},
      tags: {},
      bpm: {},
    };
  }

  cancelEdit() {
    this.store.dispatch(new ResetEditingTracks());
    this.hasSendForm = true;
  }

  copyLink() {
    this.clipboardService.copyMessage(this.trackEditForm.value.buyLink);
  }

  openLink() {
    window.open(this.trackEditForm.value.buyLink, '_blank');
  }

  submit() {
    this.hasSendForm = true;
    if (this.trackEditForm.pristine) {
      this.store.dispatch(new ResetEditingTracks());

      return;
    }
    const tracksId = this.store
      .selectSnapshot(EditingTrackState.tracks)
      .map(data => data.id);
    if (this.isMultiple) {
      this.submitMultiple(tracksId);
    } else {
      this.submitSingle(tracksId[0]);
    }
  }

  submitSingle(trackId: number) {
    const editedTrack: Partial<TrackApiModel> = {};
    for (const key of ['title', 'artist', 'album']) {
      if (!this.trackEditForm.get(key).pristine) {
        editedTrack[key] = mSanitize(this.trackEditForm.get(key).value.trim() || null);
      }
    }
    for (const key of ['bpm', 'year']) {
      if (!this.trackEditForm.get(key).pristine) {
        editedTrack[key] = this.trackEditForm.get(key).value;
      }
    }
    if (!this.trackEditForm.get('buyLink').pristine) {
      editedTrack['buy_link'] = this.trackEditForm.get('buyLink').value.trim() || null;
    }
    let tagsEdited = false;
    if (!this.trackEditForm.get('tags').pristine) {
      amplitude.getInstance().logEvent('edit_tags_panel');
      tagsEdited = true;
      this.store.dispatch(
        new TrackTagsSaveRequest(trackId, this.trackEditForm.get('tags').value),
      );
    }
    if (Object.keys(editedTrack).length) {
      this.store.dispatch(new TrackEditRequest([trackId], editedTrack));
    } else if (!tagsEdited) {
      this.store.dispatch(new ResetEditingTracks());
    }
  }

  submitMultiple(tracksId: number[]) {
    const editedTrack: Partial<TrackApiModel> = {};
    for (const key of ['title', 'artist', 'album']) {
      if (
        !this.trackEditForm.get(key).pristine &&
        this.trackEditForm.get(key).value.trim().length > 0
      ) {
        editedTrack[key] = mSanitize(this.trackEditForm.get(key).value);
      }
    }
    for (const key of ['bpm', 'year']) {
      if (
        !this.trackEditForm.get(key).pristine &&
        Number.isInteger(this.trackEditForm.get(key).value)
      ) {
        editedTrack[key] = this.trackEditForm.get(key).value;
      }
    }

    if (this.trackEditForm.get('buyLink').value.length > 0) {
      editedTrack['buy_link'] = this.trackEditForm.get('buyLink').value;
    }
    if (Object.keys(editedTrack).length) {
      this.modalService.editMultipleTrack(tracksId, editedTrack);
    } else {
      this.store.dispatch(new ResetEditingTracks());
    }
  }

  hasToShowConfirmOnExit(): boolean {
    return true;
  }

  isDirty(): boolean {
    return (
      this.trackEditForm &&
      !this.trackEditForm.pristine &&
      !isEqualFormData(this.trackEditForm.getRawValue(), this.initialFormData)
    );
  }

  closeAction(action: any) {
    this.store.dispatch(new SetActionAfterEditTrack(action));
    this.submit();
  }
  canSave(): boolean {
    return true;
  }
}
